import { ContentCopy, WarningAmberOutlined } from "@mui/icons-material";
import { Box, Button, Link, Typography } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import useAssets from "hooks/assets/useAssets";
import useAnalytics from "hooks/analytics/useAnalytics";
import useFeatureFlag from "hooks/useFeatureFlag";
import theme from "lib/theme";
import { assetsEnrolledOrLockedFilterPath } from "pages/Assets/assetsFilterLinks";

const surface = "#f0f7ff";
const border = "#d1e9ff";
const badgeBg = "#c8e4ff";
const titleColor = "#1a1a1a";
const bodyColor = "#5c5c5c";

type EnrolledAssetsV2Props = {
  enrolledAssetsCount: number;
};

export default function EnrolledAssetsV2({ enrolledAssetsCount }: EnrolledAssetsV2Props) {
  const { getAllSerialsNumbers } = useAssets();
  const { createAnalyticsEvent } = useAnalytics();
  const { enabled: assetsNewFiltersEnabled } = useFeatureFlag("assets_new_filters_enabled");
  const assetsEnrolledFilter = assetsEnrolledOrLockedFilterPath(assetsNewFiltersEnabled === true);

  const handleCopyToClipboard = () => {
    getAllSerialsNumbers(undefined, true);
    createAnalyticsEvent("copied_enrolled_asset_serials");
  };

  const title =
    enrolledAssetsCount > 1
      ? `You have ${enrolledAssetsCount} devices that require action`
      : `You have ${enrolledAssetsCount} device that requires action`;

  return (
    <Box
      sx={{
        position: "relative",
        py: 2,
        px: 2.5,
        borderRadius: 1,
        border: `1px solid ${border}`,
        bgcolor: surface,
      }}
    >
      <Box
        component="span"
        sx={{
          position: "absolute",
          top: 16,
          right: 16,
          px: 1.5,
          py: 0.5,
          borderRadius: 999,
          bgcolor: badgeBg,
          typography: "caption",
          fontWeight: 700,
          letterSpacing: "0.04em",
          color: theme.palette.primary.main,
          lineHeight: 1.2,
        }}
      >
        LOCKED DEVICES
      </Box>

      <Box sx={{ display: "flex", gap: 2, alignItems: "flex-start" }}>
        <WarningAmberOutlined
          sx={{
            color: theme.palette.primary.main,
            fontSize: 36,
            flexShrink: 0,
            mt: 0.25,
          }}
        />

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle1" fontWeight={700} sx={{ color: titleColor }}>
            {title}
          </Typography>

          <Typography variant="body2" sx={{ color: bodyColor, mt: 1 }}>
            Devices from current and previous pickups are still locked.{" "}
            <Link
              href="https://support.apple.com/guide/apple-business-manager/turn-off-activation-lock-axm812df1dd8/web"
              target="_blank"
              rel="noopener noreferrer"
              sx={{ color: theme.palette.primary.main, fontWeight: 500 }}
            >
              Remove Activation Locks
            </Link>{" "}
            and release devices from MDM and/or{" "}
            <Link
              href="https://support.apple.com/guide/apple-business-manager/release-devices-axmec4d28461/web"
              target="_blank"
              rel="noopener noreferrer"
              sx={{ color: theme.palette.primary.main, fontWeight: 500 }}
            >
              ABM
            </Link>{" "}
            to optimize processing times.
          </Typography>

          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 2,
              mt: 2,
              alignItems: "center",
            }}
          >
            <Button
              type="button"
              variant="outlined"
              size="medium"
              startIcon={<ContentCopy sx={{ color: theme.palette.primary.main }} />}
              onClick={handleCopyToClipboard}
              sx={{
                textTransform: "none",
                fontWeight: 600,
                color: theme.palette.primary.main,
                borderColor: theme.palette.primary.main,
                bgcolor: "#fff",
                "&:hover": {
                  borderColor: theme.palette.primary.dark,
                  bgcolor: "#fff",
                },
              }}
            >
              Copy Serials
            </Button>
            <Button
              type="button"
              variant="contained"
              size="medium"
              component={RouterLink}
              to={assetsEnrolledFilter}
              sx={{
                textTransform: "none",
                fontWeight: 600,
                boxShadow: "none",
                "&:hover": { boxShadow: "none" },
              }}
            >
              View Assets
            </Button>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
